// SPDX-License-Identifier: AGPL-3.0-or-later

import type {AvatarCostumeDescriptor} from '@app/features/ui/avatars/AvatarCostumeResolvers';

/**
 * Contrast floor vs `--background-primary`. Stock devil-horn red on Fluxer dark is
 * ~3.1:1 and purple ~2.3:1 — keep those halo-free; black (~1.1:1) still needs glow.
 * Tune by eye.
 */
export const AVATAR_COSTUME_CONTRAST_MIN_RATIO = 2.2;

const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const RGB_RE = /^rgba?\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})(?:\s*,\s*([0-9.]+))?\s*\)$/i;

function srgbChannelToLinear(channel: number): number {
	const value = channel / 255;
	return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance(r: number, g: number, b: number): number {
	return 0.2126 * srgbChannelToLinear(r) + 0.7152 * srgbChannelToLinear(g) + 0.0722 * srgbChannelToLinear(b);
}

/** Parse `#rgb` / `#rrggbb` / `rgb()` / `rgba()` into 0–255 channels. */
export function parseCssColorChannels(color: string): {r: number; g: number; b: number} | null {
	const trimmed = color.trim();
	const hexMatch = HEX_RE.exec(trimmed);
	if (hexMatch != null) {
		const hex = hexMatch[1];
		if (hex.length === 3) {
			return {
				r: Number.parseInt(hex[0] + hex[0], 16),
				g: Number.parseInt(hex[1] + hex[1], 16),
				b: Number.parseInt(hex[2] + hex[2], 16),
			};
		}
		return {
			r: Number.parseInt(hex.slice(0, 2), 16),
			g: Number.parseInt(hex.slice(2, 4), 16),
			b: Number.parseInt(hex.slice(4, 6), 16),
		};
	}
	const rgbMatch = RGB_RE.exec(trimmed);
	if (rgbMatch != null) {
		return {
			r: Number(rgbMatch[1]),
			g: Number(rgbMatch[2]),
			b: Number(rgbMatch[3]),
		};
	}
	return null;
}

export function contrastRatio(foreground: string, background: string): number | null {
	const fg = parseCssColorChannels(foreground);
	const bg = parseCssColorChannels(background);
	if (fg == null || bg == null) return null;
	const a = relativeLuminance(fg.r, fg.g, fg.b);
	const b = relativeLuminance(bg.r, bg.g, bg.b);
	const lighter = Math.max(a, b);
	const darker = Math.min(a, b);
	return (lighter + 0.05) / (darker + 0.05);
}

/** Chat / member-list chrome behind avatars (resolved to `rgb()` / `rgba()`). */
export function readAvatarCostumeSurfaceColor(
	root: Element | null | undefined = typeof document !== 'undefined' ? document.documentElement : null,
): string | null {
	if (root == null || typeof getComputedStyle !== 'function' || typeof document === 'undefined') {
		return null;
	}
	const probe = document.createElement('div');
	probe.style.cssText =
		'position:absolute;left:-9999px;top:0;width:1px;height:1px;pointer-events:none;background-color:var(--background-primary)';
	root.appendChild(probe);
	const value = getComputedStyle(probe).backgroundColor.trim();
	probe.remove();
	if (value.length === 0 || value === 'transparent' || value === 'rgba(0, 0, 0, 0)') {
		return null;
	}
	return value;
}

/**
 * True when the costume fill does not read clearly on the surface (or the surface
 * cannot be measured — prefer glow over vanishing art).
 */
export function costumeColorNeedsHalo(
	fillColor: string,
	surfaceColor: string | null = readAvatarCostumeSurfaceColor(),
	minRatio = AVATAR_COSTUME_CONTRAST_MIN_RATIO,
): boolean {
	if (surfaceColor == null) return true;
	const ratio = contrastRatio(fillColor, surfaceColor);
	if (ratio == null) return true;
	return ratio < minRatio;
}

function channelLuminance(color: string): number | null {
	const channels = parseCssColorChannels(color);
	if (channels == null) return null;
	return relativeLuminance(channels.r, channels.g, channels.b);
}

/**
 * Near-white interiors + dark shell (Default catface whiskers) — ear-only glow.
 * Gold trim is not near-white; those hats use `haloFill` and a root halo.
 */
function isTwoToneLightOnDark(accent: string, accentDark: string): boolean {
	const light = channelLuminance(accent);
	const dark = channelLuminance(accentDark);
	if (light == null || dark == null) return false;
	return light >= 0.8 && dark <= 0.2;
}

type AccentedCostume = Extract<AvatarCostumeDescriptor, {art: 'accented'}>;

/** Outer silhouette for contrast. Omit `haloFill` (or `'accent'`) to use `accent`. */
function silhouetteFillForHalo(costume: AccentedCostume): string {
	return costume.haloFill === 'accentDark' ? costume.accentDark : costume.accent;
}

function withRootHalo(costume: AvatarCostumeDescriptor, needsHalo: boolean): AvatarCostumeDescriptor {
	if (needsHalo) {
		if (costume.halo === 'none') {
			const {halo: _halo, ...rest} = costume;
			return rest;
		}
		return costume;
	}
	if (costume.halo === 'none') return costume;
	return {...costume, halo: 'none'};
}

/**
 * Force a contrast halo when fills sink into `--background-primary`, and strip
 * halo when contrast is already enough. Applies to every art kind.
 *
 * `haloFill` picks the silhouette slot and does not require `partHalo`.
 * `partHalo` is marked-part glow (Default catface ears, devil-horn-ring tail).
 * Dyed cats use the root-halo rule.
 */
export function applyContrastAwareHalo(
	costume: AvatarCostumeDescriptor,
	surfaceColor: string | null = readAvatarCostumeSurfaceColor(),
): AvatarCostumeDescriptor {
	if (costume.art === 'accented') {
		if (costume.partHalo && isTwoToneLightOnDark(costume.accent, costume.accentDark)) {
			const earsNeedHalo = costumeColorNeedsHalo(costume.accentDark, surfaceColor);
			return {...costume, halo: 'none', partHaloActive: earsNeedHalo};
		}
		if (costume.partHalo) {
			const partFill = costume.haloFill === 'accent' ? costume.accentDark : costume.accent;
			const partNeedsHalo = costumeColorNeedsHalo(partFill, surfaceColor);
			const bodyNeedsHalo = costumeColorNeedsHalo(silhouetteFillForHalo(costume), surfaceColor);
			if (partNeedsHalo && !bodyNeedsHalo) {
				return {...costume, halo: 'none', partHaloActive: true};
			}
			const cleared = costume.partHaloActive === true ? {...costume, partHaloActive: false} : costume;
			return withRootHalo(cleared, bodyNeedsHalo || partNeedsHalo);
		}
		const needsHalo = costumeColorNeedsHalo(silhouetteFillForHalo(costume), surfaceColor);
		const cleared = costume.partHaloActive === true ? {...costume, partHaloActive: false} : costume;
		return withRootHalo(cleared, needsHalo);
	}
	const fills = costume.art === 'silhouette' ? [costume.tint] : ['#f2ebe0'];
	const needsHalo = fills.some((fill) => costumeColorNeedsHalo(fill, surfaceColor));
	return withRootHalo(costume, needsHalo);
}
